import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'ramda'
import { useFields } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import type { InvoiceLineItem } from '~/types/entities/finance'
import {
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/utils/invoiceUtils'
import { getIsCreatedPrescriptionChewyActiveRx } from '~/utils/prescription'

export const useChargeDetailsFields = (
  item: InvoiceLineItem | RetailOrderLineItem,
) => {
  const { t } = useTranslation(['Common', 'Prices', 'Tooltips'])
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  const isChewyActiveRx = isRetailOrderLineItem
    ? true
    : getIsCreatedPrescriptionChewyActiveRx(item.prescriptionType, item.origin)

  const { fields, reset } = useFields(
    [
      {
        name: 'quantity',
        label:
          !isRetailOrderLineItem && item.prepaid
            ? t('Common:QUANTITY_PAID')
            : t('Common:QUANTITY'),
        initialValue: isGroupedInvoiceItem(item) ? 1 : item.quantity,
        type: 'number',
      },
      {
        name: 'usedQuantity',
        label: t('Common:QUANTITY_USED'),
        initialValue:
          !isRetailOrderLineItem &&
          item.prepaid &&
          Number.isInteger(item.usedQuantity)
            ? item.usedQuantity
            : undefined,
        type: 'number',
      },
      {
        name: 'remainingQuantity',
        label: t('Common:QUANTITY_REMAINING'),
        initialValue:
          !isRetailOrderLineItem &&
          item.prepaid &&
          Number.isInteger(item.prepaidRemaining)
            ? item.prepaidRemaining
            : undefined,
        type: 'number',
      },
      {
        name: 'catalogUnitPrice',
        label: isChewyActiveRx
          ? t('Prices:CHEWY_PRICE')
          : t('Prices:CATALOG_UNIT_PRICE'),
        // For now it was decided to be the same value as price
        initialValue: isGroupedInvoiceItem(item)
          ? item.subTotal
          : R.pathOr(item?.price, ['price', 'price'], item),
        type: 'number',
      },
      {
        name: 'sellPrice',
        label: t('Prices:SELL_PRICE'),
        initialValue: isGroupedInvoiceItem(item)
          ? item.subTotal
          : R.pathOr(item?.price, ['price', 'price'], item),
        type: 'number',
      },
      {
        name: 'taxed',
        label: t('Common:TAXABLE'),
        initialValue: String(
          Number(isRetailOrderLineItem ? Boolean(item.taxAmount) : item.taxed),
        ),
        type: 'select',
      },
      {
        name: 'tax',
        label: t('Common:TAX'),
        initialValue: R.propOr(0, 'taxAmount', item),
        type: 'number',
      },
      {
        name: 'discountAmount',
        label: t('Common:ITEM_DISCOUNT'),
        initialValue: R.propOr(0, 'discountAmount', item),
        type: 'number',
      },
      {
        name: 'shippingAmount',
        label: t('Common:SHIPPING'),
        initialValue: R.propOr(0, 'shippingAmount', item),
        type: 'number',
      },
      {
        name: 'retailDeliveryFee',
        label: t('Common:RETAIL_DELIVERY_FEE'),
        initialValue: R.propOr(0, 'retailDeliveryFee', item),
        type: 'number',
      },
      {
        name: 'itemSubtotal',
        label: t('Common:ITEM_SUBTOTAL'),
        initialValue: isRetailOrderLineItem ? item.totalPrice : item.subTotal,
        type: 'number',
      },
    ],
    false,
  )

  useEffect(() => {
    reset()
  }, [item])

  return fields
}
